import { Accordion, AccordionItem } from '@heroui/react';
import { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { IoInformationCircleOutline } from 'react-icons/io5';
import deepgramData from '@/assets/deepgram.json';
import fishVoices from '@/assets/fish-voices.json';
import gradiumVoices from '@/assets/gradium-voices.json';
import { FormAutoComplete, FormCheckBox, FormInput, Tooltip } from '@/core/ui';
import type { AiConfigBaseName, IStationSchema } from './schema';

interface IFishVoiceRow {
  voiceId: string;
  voiceName: string;
  sex: string;
  ageBand: string;
  volume: number;
}

const TTS_PROVIDER_OPTIONS = [
  { key: 'elevenlabs', label: 'ElevenLabs' },
  { key: 'cartesia', label: 'Cartesia' },
  { key: 'deepgram', label: 'Deepgram' },
  // { key: 'openai', label: 'OpenAI' },
  // { key: 'minimax', label: 'MiniMax' },
  { key: 'rime', label: 'Rime' },
  { key: 'inworld', label: 'Inworld' },
  { key: 'xai', label: 'xAI' },
  { key: 'fishaudio', label: 'Fish Audio' },
  { key: 'gradium', label: 'Gradium' },
  { key: 'gemini', label: 'Gemini' },
];

interface ITtsPlanPricing {
  ship: string;
  scale: string;
}

const LIVEKIT_TTS_PRICING_UNAVAILABLE = 'Ship/Scale pricing not available in LiveKit table';

const LIVEKIT_TTS_PROVIDER_PRICING: Record<string, ITtsPlanPricing> = {
  elevenlabs: { ship: '150.00', scale: '60.00' },
  cartesia: { ship: '50.00', scale: '37.50' },
  deepgram: { ship: '30.00', scale: '27.00' },
  inworld: { ship: '35.00', scale: '30.00' },
  xai: { ship: '15.00', scale: '15.00' },
  fishaudio: { ship: '15.00', scale: '15.00' },
  gradium: { ship: '48.00', scale: '36.00' },
};

const LIVEKIT_TTS_MODEL_PRICING: Record<string, ITtsPlanPricing> = {
  'rime/arcana': { ship: '40.00', scale: '30.00' },
  'rime/coda': { ship: '50.00', scale: '50.00' },
  'rime/mist': { ship: '30.00', scale: '20.00' },
  'rime/mistv2': { ship: '30.00', scale: '20.00' },
  'rime/mistv3': { ship: '30.00', scale: '20.00' },
  'inworld/inworld-tts-2': { ship: '25.00', scale: '15.00' },
  'inworld/inworld-tts-1.5-mini': { ship: '15.00', scale: '8.00' },
  'inworld/inworld-tts-1.5-max': { ship: '35.00', scale: '20.00' },
  'fishaudio/s2-pro': { ship: '15.00', scale: '15.00' },
  'fishaudio/s2.1-pro': { ship: '15.00', scale: '15.00' },
  'fishaudio/s2.1-pro-free': { ship: '0.00', scale: '0.00' },
  'fishaudio/drama-3': { ship: '10.00', scale: '10.00' },
  'gemini/gemini-3.8-flash-lite-tts': { ship: '16.00', scale: '16.00' },
  'gemini/gemini-3.8-flash-tts': { ship: '24.00', scale: '24.00' },
};

const formatLiveKitTtsPricing = (pricing: ITtsPlanPricing) => {
  return `Ship $${pricing.ship}/M chars • Scale $${pricing.scale}/M chars`;
};

const addTtsPricingDescription = <T extends { key: string }>(option: T, provider: string) => {
  const modelPricing =
    LIVEKIT_TTS_MODEL_PRICING[option.key] ?? LIVEKIT_TTS_MODEL_PRICING[`${provider}/${option.key}`];
  if (modelPricing) {
    return { ...option, description: formatLiveKitTtsPricing(modelPricing) };
  }

  const providerPricing = LIVEKIT_TTS_PROVIDER_PRICING[provider];
  if (providerPricing) {
    return { ...option, description: formatLiveKitTtsPricing(providerPricing) };
  }

  return { ...option, description: LIVEKIT_TTS_PRICING_UNAVAILABLE };
};

const ELEVENLABS_VOICE_OPTIONS = [
  // ── British ───────────────────────────────────────────────────────────
  {
    key: 'SOYHLrjzK2X1ezoPC6cr',
    label: 'Harry (M)',
    description: 'British • young • strong • confident • ideal for OSCE examiner or professional roles',
  },
  {
    key: 'JBFqnCBsd6RMkjVDRZzb',
    label: 'George (M)',
    description: 'British • warm • middle-aged • calm narrator • reassuring patient tone',
  },
  {
    key: 'N2lVS1w4EtoT3dr4eOWO',
    label: 'Callum (M)',
    description: 'British • gravelly • intense • dramatic • emotionally expressive',
  },
  {
    key: 'ThT5KcBeYPX3keUQqHPh',
    label: 'Dorothy (F)',
    description: 'British • pleasant • clear • young adult • polished narration',
  },

  // ── American Male ─────────────────────────────────────────────────────
  {
    key: 'pNInz6obpgDQGcFmaJgB',
    label: 'Adam (M)',
    description: 'American • deep • dark cinematic narrator • authoritative',
  },
  {
    key: 'nPczCjzI2devNBz1zQrb',
    label: 'Brian (M)',
    description: 'American • resonant • mature • trustworthy • documentary feel',
  },
  {
    key: '5Q0t7uMcjvnagumLfvZi',
    label: 'Paul (M)',
    description: 'American • professional • deep • polished corporate tone',
  },
  {
    key: '29vD33N1CtxCmqQRPOHJ',
    label: 'Drew (M)',
    description: 'American • news presenter • sharp • broadcast delivery',
  },
  {
    key: 'onwK4e9ZLuTAKqWW03F9',
    label: 'Daniel (M)',
    description: 'American • narrator • polished • calm • professional',
  },
  {
    key: 'ErXwobaYiN019PkySvjV',
    label: 'Antoni (M)',
    description: 'American • balanced • natural • versatile conversational voice',
  },
  {
    key: 'VR6AewLTigWG4xSOukaG',
    label: 'Arnold (M)',
    description: 'American • crisp • mature • articulate • clean delivery',
  },
  {
    key: 'TxGEqnHWrfWFTfGW9XjX',
    label: 'Josh (M)',
    description: 'American • character voice • energetic • animated',
  },
  {
    key: 'bVMeCyTHy58xNoL34h3p',
    label: 'Jeremy (M)',
    description: 'American • conversational • friendly • casual realism',
  },
  {
    key: 'iP95p4xoKVk53GoZ742B',
    label: 'Chris (M)',
    description: 'American • casual • character acting • youthful',
  },
  {
    key: 'D38z5RcWu1voky8WS1ja',
    label: 'Fin (M)',
    description: 'American • young adult • modern • relaxed',
  },
  {
    key: '2EiwWnXFnvU5JabPnv8n',
    label: 'Clyde (M)',
    description: 'American • neutral • versatile • general-purpose',
  },
  {
    key: 'Zlb1dXrM653N07WRdFW3',
    label: 'Joseph (M)',
    description: 'American • smooth • neutral narration • adaptable',
  },
  {
    key: 'CYw3kZ02Hs0563khs1Fj',
    label: 'Dave (M)',
    description: 'American/British • mixed accent • conversational',
  },
  {
    key: 'SAz9YHcvj6GT2YYXdXww',
    label: 'River (M)',
    description: 'American • expressive • character-heavy • emotional',
  },
  {
    key: 'g5CIjZEefAph4nQFvHAz',
    label: 'Ethan (M)',
    description: 'Young male • lively • character voice • energetic',
  },
  {
    key: 'flq6f7yk4E4fJM5XTYuZ',
    label: 'Michael (M)',
    description: 'Professional • smooth • polished • presentation-ready',
  },

  // ── American Female ───────────────────────────────────────────────────
  {
    key: '21m00Tcm4TlvDq8ikWAM',
    label: 'Rachel (F)',
    description: 'American • calm • neutral • reliable default voice',
  },
  {
    key: 'pMsXgVXv3BLzUgSXRplE',
    label: 'Serena (F)',
    description: 'Warm • professional • empathetic • ideal for OSCE patients',
  },
  {
    key: 'LcfcDJNUP1GQjkzn1xUU',
    label: 'Emily (F)',
    description: 'American • calm • meditative • soft-spoken',
  },
  {
    key: 'pFZP5JQG7iQjIQuC4Bku',
    label: 'Lily (F)',
    description: 'Professional • calm • polished • healthcare friendly',
  },
  {
    key: 'EXAVITQu4vr4xnSDxMaL',
    label: 'Bella (F)',
    description: 'American • soft • warm • young • emotionally natural',
  },
  {
    key: 'MF3mGyEYCl7XYWbV9V6O',
    label: 'Elli (F)',
    description: 'American • emotional • expressive • youthful',
  },
  {
    key: 't0jbNlBVZ17f02VDIeMI',
    label: 'Jessie (F)',
    description: 'American • neutral • friendly • versatile',
  },
  {
    key: 'jsCqWAovK2LkecY7zXl4',
    label: 'Freya (F)',
    description: 'American • expressive • energetic • youthful',
  },
  {
    key: 'AZnzlk1XvdvUeBnXmlld',
    label: 'Domi (F)',
    description: 'American • dramatic • expressive • animated character voice',
  },
  {
    key: 'XB0fDUnXU5powFXDhCwa',
    label: 'Charlotte (F)',
    description: 'Dynamic • theatrical • versatile character performance',
  },
  {
    key: 'oWAxZDx7w5VEj9dCyTzz',
    label: 'Grace (F)',
    description: 'American Southern • warm • relaxed • friendly',
  },

  // ── Other ─────────────────────────────────────────────────────────────
  {
    key: 'IKne3meq5aSn9XLyUdCD',
    label: 'Charlie (M)',
    description: 'Australian • confident • modern • conversational',
  },
  {
    key: 'zcAOhNBS3c14rBihAFp1',
    label: 'Giovanni (M)',
    description: 'Italian accent • charismatic • cinematic',
  },
  {
    key: 'jBpfuIE2acCO8z3wKNLl',
    label: 'Gigi (F)',
    description: 'Childlike • animated • playful • cartoon-style',
  },
  {
    key: 'z9fAnlkpzviPz146aGWa',
    label: 'Glinda (F)',
    description: 'Fantasy character • witch-like • theatrical • expressive',
  },
];

const ELEVENLABS_MODEL_OPTIONS = [
  { key: 'eleven_v3', label: 'Eleven v3 (latest, 70+ languages)' },
  { key: 'eleven_multilingual_v2', label: 'Eleven Multilingual v2 (29 languages)' },
  { key: 'eleven_flash_v2_5', label: 'Eleven Flash v2.5 (fast, 32 languages)' },
  { key: 'eleven_flash_v2', label: 'Eleven Flash v2 (ultra-fast, English)' },
  { key: 'eleven_multilingual_sts_v2', label: 'Eleven Multilingual STS v2 (voice changer)' },
  { key: 'eleven_english_sts_v2', label: 'Eleven English STS v2 (voice changer)' },
].map((option) => addTtsPricingDescription(option, 'elevenlabs'));

const CARTESIA_VOICE_OPTIONS = [
  {
    label: '[British] Gemma (F)',
    description: 'Decisive Agent - Confident, emotive British female for professional assistance',
    key: '62ae83ad-4f6a-430b-af41-a9bede9286ca',
  },
  {
    label: '[British] Archie (M)',
    description: 'Approachable Mate - Warm, conversational British male for casual and engaging dialogue',
    key: 'ef191366-f52f-447a-a398-ed8c0f2943a1',
  },
  {
    label: '[British] Lucy (F)',
    description: 'Capable Coordinator - Reassuring British female for customer assistance',
    key: '2f251ac3-89a9-4a77-a452-704b474ccd01',
  },
  {
    label: '[British] Julia (F)',
    description:
      'Gentle Guide - Soft, graceful tone with a composed rhythm for polished guidance and reassurance',
    key: '273f9ef7-9fc2-4def-88bb-ab108c6249ca',
  },
  {
    label: '[British] George (M)',
    description: 'Composed Consultant - Steady, British male voice for capable assistance',
    key: '4bc3cb8c-adb9-4bb8-b5d5-cbbef950b991',
  },
  {
    label: '[British] Casper (M)',
    description: 'Gentle Narrator - Wistful, young male for emotional narrations',
    key: '4f7f1324-1853-48a6-b294-4e78e8036a83',
  },
  {
    label: '[British] Alistair (M)',
    description: 'Composed Consultant - Sophisticated, steady British male for customer interactions',
    key: 'c8f7835e-28a3-4f0c-80d7-c1302ac62aae',
  },
  {
    label: '[British] Victoria (F)',
    description: 'Refined Coordinator - Crisp, professional British female for reassuring communication',
    key: 'dc30854e-e398-4579-9dc8-16f6cb2c19b9',
  },
  {
    label: '[British] Rupert (M)',
    description: 'Caring Dad - Warm, mature voice for caring, reassuring conversations',
    key: '0ad65e7f-006c-47cf-bd31-52279d487913',
  },
  {
    label: '[British] Evie (F)',
    description: 'Engaging Expert - Formal British female for high-level corporate interactions',
    key: 'e5d4c33a-d8f6-46e8-a10f-b5afecc35648',
  },
  {
    label: '[British] Benedict (M)',
    description: 'Measured Mediator - Polished, formal British male',
    key: '3c0f09d6-e0d7-499c-a594-70c5b7b93048',
  },
  {
    label: '[British] Harrison (M)',
    description: 'Diligent Detailer - Crisp, professional British male for efficient customer interactions',
    key: 'df89f42f-f285-4613-adbf-14eedcec4c9e',
  },
  {
    label: '[British] Toby (M)',
    description: 'Genuine Guide - Warm, conversational British male with a polished tone',
    key: '3d5ce2fb-e56c-42f0-9ed9-4662484063b4',
  },
  {
    label: '[British] Charlotte (F)',
    description: 'Heiress - Elegant, young adult female for narrations',
    key: '71a7ad14-091c-4e8e-a314-022ece01c121',
  },
  {
    label: '[British] Hugo (M)',
    description: 'Teatime Friend - Expressive, young adult male for characters and natural conversation',
    key: '1463a4e1-56a1-4b41-b257-728d56e93605',
  },
  {
    label: '[British] Martin (M)',
    description:
      'Meticulous Operator - A mature, composed delivery offering precise, practical guidance to ensure absolute dependability',
    key: 'dcddf1f4-b114-4b5d-9158-895cbba0e406',
  },
  {
    label: '[British] Arthur (M)',
    description:
      'Polished Advisor - Refined and confident tone for formal support and clear customer communication',
    key: 'bb7e8daa-8b79-47a2-8408-a7a1cc72b53c',
  },
  {
    label: '[British] Griffin (M)',
    description: 'Narrator - Elderly male for narrations',
    key: 'c99d36f3-5ffd-4253-803a-535c1bc9c306',
  },
  {
    label: '[British] Fiona (F)',
    description:
      'Witty Woman - Chirpy and energetic British female voice with a bright tone, great for lively and engaging conversations',
    key: 'a01c369f-6d2d-4185-bc20-b32c225eab70',
  },
  {
    label: '[British] Alec (M)',
    description:
      'Spirited Salesman - Lively, warm British male with an upbeat, welcoming energy for sales, product demos, and engaging customer conversations',
    key: '17044048-bfab-44b2-9532-9c1b65e9c217',
  },
  {
    label: '[British] Miles (M)',
    description: 'Yogi - Deep, soothing mature male for providing guidance',
    key: 'f114a467-c40a-4db8-964d-aaba89cd08fa',
  },
  {
    label: '[British] Oliver (M)',
    description: 'Customer Chap - Polite, young adult male for customer facing use cases',
    key: 'ee7ea9f8-c0c1-498c-9279-764d6b56d189',
  },
  {
    label: '[British] Benedict (Royal) (M)',
    description: 'Royal Narrator - Confident, firm male for narrations',
    key: '7cf0e2b1-8daf-4fe4-89ad-f6039398f359',
  },
  {
    label: '[British] Pippa (F)',
    description:
      'Bright Assistant - Bright, upbeat delivery for helpful guidance, friendly onboarding, and clear everyday support conversations',
    key: '81cd8d19-45e7-47b2-ad0e-bcd94f557ad0',
  },
  {
    label: '[British] Imogen (F)',
    description:
      'Polished Guide - Polished, articulate delivery for refined support, clear explanations, and calm customer guidance',
    key: '5a93ae96-9e3e-4b9d-8575-5f62b7de6d0f',
  },
  {
    label: '[British] Gary (M)',
    description: 'Composed Advisor - Measured and direct delivery for no-nonsense conversations',
    key: 'dc52ada6-0e11-4684-a8fa-e0af5b7bdcb2',
  },
  {
    label: '[British] Trevor (M)',
    description: 'Movieman - Deep, elderly male for narrations and entertainment use cases',
    key: 'c45bc5ec-dc68-4feb-8829-6e6b2748095d',
  },
  {
    label: '[British] Saira (F)',
    description:
      'Organized Coordinator - Warm, attentive delivery for organized, thoughtful follow-up, and approachable service conversations',
    key: '1e9b9b3d-d2ce-4cac-9d05-bc36a63fa28e',
  },
  {
    label: '[British] Griffin (Excited) (M)',
    description: 'Excited Narrator - Elderly male for narrations',
    key: '34d923aa-c3b5-4f21-aac7-2c1f12730d4b',
  },
  {
    label: '[British] Caspian (M)',
    description: 'Oracle - Echo-y, mystical male for characters with gravitas',
    key: 'd7862948-75c3-4c7c-ae28-2959fe166f49',
  },
  {
    label: '[British] Oscar (M)',
    description:
      'Clear Specialist - Keen, professional delivery with an energetic undertone for practical guidance, accurate explanations, and professional service conversations',
    key: '22df7143-7987-4e15-a720-d65c69a443b3',
  },
  {
    label: '[British] Ailsa (F)',
    description:
      'Warm Guide - Calm and personable delivery suited to friendly guidance and calm service interactions',
    key: 'fb02b554-7d64-4f90-841e-e57fc88f410c',
  },
  {
    label: '[British] Roy (M)',
    description:
      'Stern Realist - Measured, professional delivery that channels a serious and experienced business attitude into trusted support',
    key: 'f2ddbdca-59d9-4363-abeb-a197d65ea24a',
  },
  {
    label: '[British] Alfie (M)',
    description:
      'Composed Advisor - Calm, balanced delivery for thoughtful guidance and professional assistant experiences',
    key: '5e7d492a-5502-482e-b315-ebf587427806',
  },
  {
    label: '[British] Jasper (M)',
    description: 'Service Specialist - Warm, expressive voice for customer support and sales conversations',
    key: '3faa81ae-d3d8-4ab1-9e44-e50e46d33c30',
  },
  {
    label: '[British] Evelyn (F)',
    description:
      'Digital Assistant - A clear, neutral, and precise female voice with a smooth digital polish - perfect for virtual-assistant style responses, instructions, and general interactions',
    key: '3c7dfd17-3fa8-47aa-aacc-6313fe025442',
  },
];

const CARTESIA_MODEL_OPTIONS = [
  // Best for latest features and speed (90ms latency)
  { key: 'sonic-3.5', label: 'Sonic 3.5 (latest stable <100ms)' },
  { key: 'sonic-3', label: 'Sonic 3' },
  { key: 'sonic-turbo', label: 'Sonic Turbo (Fastest <50ms)' },
].map((option) => addTtsPricingDescription(option, 'cartesia'));

interface IDeepgramVoiceRow {
  name: string;
  model_id: string;
  gender: string;
  characteristics: string;
}
interface IDeepgramModelFamily {
  family_name: string;
  voices: IDeepgramVoiceRow[];
}

const DEEPGRAM_FAMILY_SLUG: Record<string, string> = {
  'Flux TTS': 'flux-tts',
  'Aura-2': 'aura-2',
};

const DEEPGRAM_MODEL_FAMILIES = (deepgramData as { model_families: IDeepgramModelFamily[] }).model_families;

const DEEPGRAM_MODEL_OPTIONS = DEEPGRAM_MODEL_FAMILIES.map((family) =>
  addTtsPricingDescription(
    { key: DEEPGRAM_FAMILY_SLUG[family.family_name] ?? family.family_name, label: family.family_name },
    'deepgram',
  ),
);

const DEEPGRAM_VOICE_OPTIONS_BY_MODEL = new Map(
  DEEPGRAM_MODEL_FAMILIES.map((family) => [
    DEEPGRAM_FAMILY_SLUG[family.family_name] ?? family.family_name,
    family.voices.map((voice) => ({
      key: voice.name.toLowerCase(),
      label: `${voice.name} (${voice.gender})`,
      description: voice.characteristics,
    })),
  ]),
);
const DEEPGRAM_ALL_VOICE_OPTIONS = [...DEEPGRAM_VOICE_OPTIONS_BY_MODEL.values()].flat();

const _OPENAI_MODEL_OPTIONS = [
  { key: 'gpt-4o-mini-tts', label: 'GPT-4o Mini TTS (latest, all 13 voices)' },
  { key: 'tts-1-hd', label: 'TTS-1 HD (high quality)' },
  { key: 'tts-1', label: 'TTS-1 (low latency)' },
].map((option) => addTtsPricingDescription(option, 'openai'));

const _OPENAI_VOICE_OPTIONS = [
  {
    key: 'fable',
    label: 'Fable (M)',
    description: 'British-accented • warm storyteller • expressive narration',
  },
  {
    key: 'alloy',
    label: 'Alloy (Neutral)',
    description: 'Balanced • neutral • versatile assistant voice',
  },
  {
    key: 'ash',
    label: 'Ash (M)',
    description: 'Calm • measured • smooth conversational tone',
  },
  {
    key: 'ballad',
    label: 'Ballad (M)',
    description: 'Emotive • dramatic • storytelling-focused',
  },
  {
    key: 'cedar',
    label: 'Cedar (M)',
    description: 'Grounded • natural • relaxed delivery',
  },
  {
    key: 'coral',
    label: 'Coral (F)',
    description: 'Clear • warm • friendly conversational voice',
  },
  {
    key: 'echo',
    label: 'Echo (M)',
    description: 'Crisp • professional • articulate assistant tone',
  },
  {
    key: 'marin',
    label: 'Marin (F)',
    description: 'Smooth • conversational • calm and natural',
  },
  {
    key: 'nova',
    label: 'Nova (F)',
    description: 'Energetic • friendly • modern assistant voice',
  },
  {
    key: 'onyx',
    label: 'Onyx (M)',
    description: 'Deep • authoritative • cinematic narrator',
  },
  {
    key: 'sage',
    label: 'Sage (F)',
    description: 'Thoughtful • composed • professional tone',
  },
  {
    key: 'shimmer',
    label: 'Shimmer (F)',
    description: 'Soft • gentle • soothing delivery',
  },
  {
    key: 'verse',
    label: 'Verse (M)',
    description: 'Dynamic • expressive • energetic narration',
  },
];

const _MINIMAX_MODEL_OPTIONS = [
  { key: 'speech-2.6-hd', label: 'Speech 2.6 HD (latest, high quality)' },
  { key: 'speech-2.6-turbo', label: 'Speech 2.6 Turbo (latest, fast)' },
  { key: 'speech-02-hd', label: 'Speech 02 HD' },
  { key: 'speech-02-turbo', label: 'Speech 02 Turbo' },
  { key: 'speech-2.5-hd-preview', label: 'Speech 2.5 HD Preview' },
  { key: 'speech-2.5-turbo-preview', label: 'Speech 2.5 Turbo Preview' },
].map((option) => addTtsPricingDescription(option, 'minimax'));

const _MINIMAX_VOICE_OPTIONS = [
  // ── Male ──────────────────────────────────────────────────────────────
  {
    key: 'English_expressive_narrator',
    label: 'Expressive Narrator (M)',
    description: 'Rich • cinematic • storytelling-focused • expressive narration',
  },
  {
    key: 'English_WiseScholar',
    label: 'Wise Scholar (M)',
    description: 'Academic • thoughtful • intelligent • educational tone',
  },
  {
    key: 'English_Steadymentor',
    label: 'Steady Mentor (M)',
    description: 'Reliable • calm • guiding • reassuring delivery',
  },
  {
    key: 'English_MaturePartner',
    label: 'Mature Partner (M)',
    description: 'Composed • experienced • conversational • grounded',
  },
  {
    key: 'English_PatientMan',
    label: 'Patient Man (M)',
    description: 'Calm • unhurried • gentle • patient-friendly',
  },
  {
    key: 'English_Trustworth_Man',
    label: 'Trustworthy Man (M)',
    description: 'Dependable • clear • professional • reassuring',
  },
  {
    key: 'English_magnetic_voiced_man',
    label: 'Magnetic Man (M)',
    description: 'Engaging • charismatic • captivating conversational tone',
  },
  {
    key: 'English_ManWithDeepVoice',
    label: 'Deep Voice Man (M)',
    description: 'Deep • commanding • authoritative • cinematic',
  },
  {
    key: 'English_Deep-VoicedGentleman',
    label: 'Deep Gentleman (M)',
    description: 'Refined • deep • polished • mature narration',
  },
  {
    key: 'English_Gentle-voiced_man',
    label: 'Gentle Man (M)',
    description: 'Soft • approachable • warm • calming',
  },
  {
    key: 'English_FriendlyPerson',
    label: 'Friendly Person (M)',
    description: 'Warm • casual • conversational • approachable',
  },
  {
    key: 'English_DecentYoungMan',
    label: 'Young Man (M)',
    description: 'Polite • youthful • clear • modern tone',
  },
  {
    key: 'English_Diligent_Man',
    label: 'Diligent Man (M)',
    description: 'Focused • earnest • professional • composed',
  },
  {
    key: 'English_BossyLeader',
    label: 'Bossy Leader (M)',
    description: 'Authoritative • direct • commanding • assertive',
  },
  {
    key: 'English_Debator',
    label: 'Debater (M)',
    description: 'Articulate • assertive • energetic speaking style',
  },
  {
    key: 'English_Jovialman',
    label: 'Jovial Man (M)',
    description: 'Cheerful • upbeat • friendly • lively',
  },
  {
    key: 'English_Comedian',
    label: 'Comedian (M)',
    description: 'Humorous • playful • expressive character voice',
  },
  {
    key: 'English_PassionateWarrior',
    label: 'Warrior (M)',
    description: 'Intense • driven • dramatic • cinematic energy',
  },
  {
    key: 'English_ReservedYoungMan',
    label: 'Reserved Young Man (M)',
    description: 'Quiet • measured • introverted • subtle tone',
  },
  {
    key: 'English_Strong-WilledBoy',
    label: 'Strong-Willed Boy (M)',
    description: 'Youthful • determined • energetic • spirited',
  },
  {
    key: 'English_SadTeen',
    label: 'Sad Teen (M)',
    description: 'Melancholic • subdued • emotional • soft-spoken',
  },
  {
    key: 'English_Aussie_Bloke',
    label: 'Aussie Bloke (M)',
    description: 'Australian accent • casual • relaxed • friendly',
  },

  // ── Female ────────────────────────────────────────────────────────────
  {
    key: 'English_CaptivatingStoryteller',
    label: 'Storyteller (F)',
    description: 'Engaging • narrative-focused • expressive storytelling',
  },
  {
    key: 'English_Wiselady',
    label: 'Wise Lady (F)',
    description: 'Composed • authoritative • intelligent • mature',
  },
  {
    key: 'English_ConfidentWoman',
    label: 'Confident Woman (F)',
    description: 'Assured • clear • professional • articulate',
  },
  {
    key: 'English_Graceful_Lady',
    label: 'Graceful Lady (F)',
    description: 'Elegant • refined • polished • calm narration',
  },
  {
    key: 'English_SereneWoman',
    label: 'Serene Woman (F)',
    description: 'Calm • soothing • gentle • reassuring tone',
  },
  {
    key: 'English_Kind-heartedGirl',
    label: 'Kind Girl (F)',
    description: 'Warm • caring • empathetic • patient-friendly',
  },
  {
    key: 'English_Soft-spokenGirl',
    label: 'Soft-Spoken Girl (F)',
    description: 'Gentle • quiet • soft delivery • calming',
  },
  {
    key: 'English_compelling_lady1',
    label: 'Compelling Lady (F)',
    description: 'Persuasive • polished • professional • engaging',
  },
  {
    key: 'English_captivating_female1',
    label: 'Captivating Female (F)',
    description: 'Dynamic • engaging • expressive conversational voice',
  },
  {
    key: 'English_Upbeat_Woman',
    label: 'Upbeat Woman (F)',
    description: 'Energetic • positive • cheerful • lively',
  },
  {
    key: 'English_radiant_girl',
    label: 'Radiant Girl (F)',
    description: 'Bright • vivid • youthful • expressive',
  },
  {
    key: 'English_LovelyGirl',
    label: 'Lovely Girl (F)',
    description: 'Warm • friendly • soft • approachable',
  },
  {
    key: 'English_PlayfulGirl',
    label: 'Playful Girl (F)',
    description: 'Cheerful • light-hearted • animated • fun',
  },
  {
    key: 'English_WhimsicalGirl',
    label: 'Whimsical Girl (F)',
    description: 'Imaginative • playful • quirky • expressive',
  },
  {
    key: 'English_MatureBoss',
    label: 'Mature Boss (F)',
    description: 'Commanding • experienced • confident leadership tone',
  },
  {
    key: 'English_AssertiveQueen',
    label: 'Assertive Queen (F)',
    description: 'Strong • authoritative • regal • confident',
  },
  {
    key: 'English_ImposingManner',
    label: 'Imposing Manner (F)',
    description: 'Powerful • regal • dramatic • commanding presence',
  },
  {
    key: 'English_SentimentalLady',
    label: 'Sentimental Lady (F)',
    description: 'Emotional • expressive • heartfelt narration',
  },
  {
    key: 'English_StressedLady',
    label: 'Stressed Lady (F)',
    description: 'Tense • urgent • anxious • emotional realism',
  },
  {
    key: 'English_Whispering_girl',
    label: 'Whispering Girl (F)',
    description: 'Hushed • intimate • soft whisper-style delivery',
  },
  {
    key: 'English_AnimeCharacter',
    label: 'Anime Character (F)',
    description: 'Stylised • animated • exaggerated expressive tone',
  },
];

const _MINIMAX_EMOTION_OPTIONS = [
  { key: 'happy', label: 'Happy' },
  { key: 'sad', label: 'Sad' },
  { key: 'angry', label: 'Angry' },
  { key: 'fearful', label: 'Fearful' },
  { key: 'disgusted', label: 'Disgusted' },
  { key: 'surprised', label: 'Surprised' },
  { key: 'calm', label: 'Calm' },
  { key: 'neutral', label: 'Neutral' },
];

const CartesiaEmotionOptions = [
  { key: 'neutral', label: 'Neutral' },
  { key: 'calm', label: 'Calm' },
  { key: 'content', label: 'Content' },
  { key: 'excited', label: 'Excited' },
  { key: 'sad', label: 'Sad' },
  { key: 'angry', label: 'Angry' },
  { key: 'scared', label: 'Scared' },
];

const RIME_MODEL_OPTIONS = [
  { key: 'arcana', label: 'Rime Arcana' },
  { key: 'coda', label: 'Rime Coda' },
  { key: 'mist', label: 'Rime Mist' },
  { key: 'mistv2', label: 'Rime Mist v2' },
  { key: 'mistv3', label: 'Rime Mist v3' },
].map((option) => addTtsPricingDescription(option, 'rime'));

const INWORLD_MODEL_OPTIONS = [
  { key: 'inworld-tts-2', label: 'Inworld TTS 2.0' },
  { key: 'inworld-tts-1.5-max', label: 'Inworld TTS 1.5 Max' },
  { key: 'inworld-tts-1.5-mini', label: 'Inworld TTS 1.5 Mini' },
].map((option) => addTtsPricingDescription(option, 'inworld'));

const RIME_VOICE_OPTIONS = [
  // ── mistv2 ───────────────────────────────────────────────────────────
  {
    key: 'brenda',
    label: 'Brenda (F)',
    description: 'British • Elder • wise • playful • warm • unhurried • model: mistv2',
  },
  {
    key: 'carol',
    label: 'Carol (F)',
    description: 'British • Young Adult • professional • smart • polished • steady • model: mistv2',
  },
  {
    key: 'geoff',
    label: 'Geoff (M)',
    description: 'British • Adult • smart • soothing • calm • polished • model: mistv2',
  },
  {
    key: 'gerald',
    label: 'Gerald (M)',
    description: 'Black British • Young Adult • friendly • upbeat • warm • lively • model: mistv2',
  },
  {
    key: 'kenneth',
    label: 'Kenneth (M)',
    description: 'British • Young Adult • cool • upbeat • lively • casual • model: mistv2',
  },
  {
    key: 'marge',
    label: 'Marge (F)',
    description: 'British • Elder • empathetic • wise • warm • grounded • model: mistv2',
  },
  {
    key: 'maya',
    label: 'Maya (F)',
    description: 'Black British • Young Adult • professional • upbeat • warm • lively • model: mistv2',
  },
  {
    key: 'nyles',
    label: 'Nyles (M)',
    description: 'British • Adult • professional • upbeat • polished • warm • model: mistv2',
  },
  {
    key: 'rex',
    label: 'Rex (M)',
    description: 'British • Young Adult • cool • smart • polished • steady • model: mistv2',
  },
  {
    key: 'sharon',
    label: 'Sharon (F)',
    description: 'Black British • Young Adult • cool • professional • polished • warm • model: mistv2',
  },
  {
    key: 'tibur',
    label: 'Tibur (M)',
    description: 'British • Elder • friendly • wise • warm • unhurried • model: mistv2',
  },

  // ── arcana ───────────────────────────────────────────────────────────
  {
    key: 'albion',
    label: 'Albion (M)',
    description: 'British • Young Adult • calm • even-keeled • quick • casual • model: arcana',
  },
  {
    key: 'vashti',
    label: 'Vashti (F)',
    description: 'British • Adult • bright • expressive • professional • polished • model: arcana',
  },
  {
    key: 'kelly_aoife',
    label: 'Kelly Aoife (F)',
    description: 'Irish • Adult • warm • friendly • casual • model: arcana',
  },
  {
    key: 'moyo_david',
    label: 'David Moyo (M)',
    description: 'British-Nigerian • Adult • warm • grounded • friendly • model: arcana',
  },
  {
    key: 'smith_lisa',
    label: 'Lisa Smith (F)',
    description: 'British • Elder • warm • friendly • grounded • model: arcana',
  },
  {
    key: 'smith_mike',
    label: 'Mike Smith (M)',
    description: 'British • Elder • grounded • steady • warm • model: arcana',
  },
];

const INWORLD_VOICE_OPTIONS = [
  {
    key: 'Clive',
    label: 'Clive (M)',
    description: 'British • Middle Aged • calm • cordial male voice',
  },
  {
    key: 'Craig',
    label: 'Craig (M)',
    description: 'British • Old • refined • articulate older male voice',
  },
  {
    key: 'Duncan',
    label: 'Duncan (M)',
    description: 'British • Middle Aged • warm • articulate male voice',
  },
  {
    key: 'Eleanor',
    label: 'Eleanor (F)',
    description: 'British • Middle Aged • polished • approachable female voice',
  },
  {
    key: 'Felix',
    label: 'Felix (M)',
    description: 'British • Middle Aged • calm • friendly male voice',
  },
  {
    key: 'Graham',
    label: 'Graham (M)',
    description: 'British • Middle Aged • profound • authoritative male voice',
  },
  {
    key: 'Olivia',
    label: 'Olivia (F)',
    description: 'British • Middle Aged • young • friendly female voice',
  },
  {
    key: 'Ronald',
    label: 'Ronald (M)',
    description: 'British • Old • confident • deep male voice',
  },
  {
    key: 'Rupert',
    label: 'Rupert (M)',
    description: 'British • Middle Aged • resonant • commanding male voice',
  },
  {
    key: 'Sophie',
    label: 'Sophie (F)',
    description: 'British • Middle Aged • friendly female voice',
  },
  {
    key: 'Victoria',
    label: 'Victoria (F)',
    description: 'British • Middle Aged • silky • expressive female voice',
  },
  {
    key: 'Wendy',
    label: 'Wendy (F)',
    description: 'British • Old • posh • middle-aged female voice',
  },
  {
    key: 'Rosalind',
    label: 'Rosalind (F)',
    description: 'British • Middle Aged • warm • Interactive media',
  },
  {
    key: 'Freddie',
    label: 'Freddie (M)',
    description: 'British • Young Adult • friendly • approachable',
  },
  {
    key: 'Cordelia',
    label: 'Cordelia (F)',
    description: 'British • Old • warm • composed',
  },
  {
    key: 'Winifred',
    label: 'Winifred (F)',
    description: 'British • Old • calm • composed',
  },
  {
    key: 'Beatrice',
    label: 'Beatrice (F)',
    description: 'British • Old • warm • friendly',
  },
];

export const XAI_VOICE_OPTIONS = [
  {
    key: 'eve',
    label: 'Eve (F)',
    description: 'Energetic • upbeat • lively • expressive conversational tone',
  },
  {
    key: 'ara',
    label: 'Ara (F)',
    description: 'Warm • friendly • approachable • natural dialogue',
  },
  {
    key: 'rex',
    label: 'Rex (M)',
    description: 'Confident • clear • professional • articulate delivery',
  },
  {
    key: 'sal',
    label: 'Sal (Neutral)',
    description: 'Smooth • balanced • versatile assistant-style voice',
  },
  {
    key: 'leo',
    label: 'Leo (M)',
    description: 'Authoritative • strong • deep • commanding presence',
  },
];
export const XAI_MODEL_OPTIONS = [{ key: 'xai/tts-1', label: 'XAI tts' }].map((option) =>
  addTtsPricingDescription(option, 'xai'),
);

const FISHAUDIO_VOICE_VOLUME_BY_ID = new Map<string, number>();
const FISHAUDIO_VOICE_OPTIONS = (() => {
  const uniqueVoices = new Map<string, IFishVoiceRow>();

  for (const row of fishVoices as IFishVoiceRow[]) {
    if (!uniqueVoices.has(row.voiceId)) {
      uniqueVoices.set(row.voiceId, row);
      FISHAUDIO_VOICE_VOLUME_BY_ID.set(row.voiceId, row.volume);
    }
  }

  return [...uniqueVoices.values()].map((voice) => ({
    key: voice.voiceId,
    label: `${voice.voiceName} (${voice.sex})`,
    description: `${voice.ageBand} · volume ${voice.volume}`,
  }));
})();

const FISHAUDIO_MODEL_OPTIONS = [
  { key: 's2-pro', label: 'S2 Pro' },
  { key: 's2.1-pro', label: 'S2.1 Pro' },
  { key: 's2.1-pro-free', label: 'S2.1 Pro Free' },
  { key: 'drama-3', label: 'Drama 3' },
].map((option) => addTtsPricingDescription(option, 'fishaudio'));

interface IGradiumVoiceRow {
  uid: string;
  name: string;
  description: string;
}

const GRADIUM_VOICE_OPTIONS = (gradiumVoices as IGradiumVoiceRow[]).map((voice) => ({
  key: voice.uid,
  label: voice.name,
  description: voice.description,
}));

const GRADIUM_MODEL_OPTIONS = [{ key: 'gradium/default', label: 'Gradium Default' }].map((option) =>
  addTtsPricingDescription(option, 'gradium'),
);

const GEMINI_VOICE_OPTIONS = [
  { key: 'Zephyr', label: 'Zephyr (F)', description: 'Bright · British English' },
  { key: 'Puck', label: 'Puck (M)', description: 'Upbeat · British English' },
  { key: 'Charon', label: 'Charon (M)', description: 'Informative · British English' },
  { key: 'Kore', label: 'Kore (F)', description: 'Firm · British English' },
  { key: 'Fenrir', label: 'Fenrir (M)', description: 'Excitable · British English' },
  { key: 'Leda', label: 'Leda (F)', description: 'Youthful · British English' },
  { key: 'Orus', label: 'Orus (M)', description: 'Firm · British English' },
  { key: 'Aoede', label: 'Aoede (F)', description: 'Breezy · British English' },
  { key: 'Callirrhoe', label: 'Callirrhoe (F)', description: 'Easy-going · British English' },
  { key: 'Autonoe', label: 'Autonoe (F)', description: 'Bright · British English' },
  { key: 'Enceladus', label: 'Enceladus (M)', description: 'Breathy · British English' },
  { key: 'Iapetus', label: 'Iapetus (M)', description: 'Clear · British English' },
  { key: 'Umbriel', label: 'Umbriel (M)', description: 'Easy-going · British English' },
  { key: 'Algieba', label: 'Algieba (M)', description: 'Smooth · British English' },
  { key: 'Despina', label: 'Despina (F)', description: 'Smooth · British English' },
  { key: 'Erinome', label: 'Erinome (F)', description: 'Clear · British English' },
  { key: 'Algenib', label: 'Algenib (M)', description: 'Gravelly · British English' },
  { key: 'Rasalgethi', label: 'Rasalgethi (M)', description: 'Informative · British English' },
  { key: 'Laomedeia', label: 'Laomedeia (F)', description: 'Upbeat · British English' },
  { key: 'Achernar', label: 'Achernar (F)', description: 'Soft · British English' },
  { key: 'Alnilam', label: 'Alnilam (M)', description: 'Firm · British English' },
  { key: 'Schedar', label: 'Schedar (M)', description: 'Even · British English' },
  { key: 'Gacrux', label: 'Gacrux (F)', description: 'Mature · British English' },
  { key: 'Pulcherrima', label: 'Pulcherrima (F)', description: 'Forward · British English' },
  { key: 'Achird', label: 'Achird (M)', description: 'Friendly · British English' },
  { key: 'Zubenelgenubi', label: 'Zubenelgenubi (M)', description: 'Casual · British English' },
  { key: 'Vindemiatrix', label: 'Vindemiatrix (F)', description: 'Gentle · British English' },
  { key: 'Sadachbia', label: 'Sadachbia (M)', description: 'Lively · British English' },
  { key: 'Sadaltager', label: 'Sadaltager (M)', description: 'Knowledgeable · British English' },
  { key: 'Sulafat', label: 'Sulafat (F)', description: 'Warm · British English' },
];

const GEMINI_MODEL_OPTIONS = [
  { key: 'gemini-3.8-flash-lite-tts', label: 'Gemini 3.8 Flash Lite TTS' },
  { key: 'gemini-3.8-flash-tts', label: 'Gemini 3.8 Flash TTS' },
].map((option) => addTtsPricingDescription(option, 'gemini'));

const labelWithTooltip = (label: string, tooltip: string) => {
  return (
    <span className='inline-flex items-center gap-1'>
      {label}
      <Tooltip content={tooltip} showArrow placement='right'>
        <span className='text-default-500 cursor-help'>
          <IoInformationCircleOutline size={16} />
        </span>
      </Tooltip>
    </span>
  );
};

export const AiConfigTtsTab = ({ baseName }: { baseName: AiConfigBaseName }) => {
  const form = useFormContext<IStationSchema>();
  const fishaudioVoice = useWatch({
    control: form.control,
    name: `${baseName}.tts.fishaudio.voice`,
  });

  const fishaudioVoiceOptions = useMemo(() => {
    const currentVoice = fishaudioVoice?.trim();
    if (!currentVoice) return FISHAUDIO_VOICE_OPTIONS;

    const isInCatalog = FISHAUDIO_VOICE_OPTIONS.some((option) => option.key === currentVoice);
    if (isInCatalog) return FISHAUDIO_VOICE_OPTIONS;

    return [...FISHAUDIO_VOICE_OPTIONS, { key: currentVoice, label: currentVoice }];
  }, [fishaudioVoice]);

  const deepgramModel = useWatch({
    control: form.control,
    name: `${baseName}.tts.deepgram.model`,
  });
  const deepgramVoice = useWatch({
    control: form.control,
    name: `${baseName}.tts.deepgram.voice`,
  });

  const deepgramVoiceOptions = useMemo(() => {
    const options = DEEPGRAM_VOICE_OPTIONS_BY_MODEL.get(deepgramModel ?? '') ?? DEEPGRAM_ALL_VOICE_OPTIONS;

    const currentVoice = deepgramVoice?.trim();
    if (!currentVoice) return options;

    const isInCatalog = options.some((option) => option.key === currentVoice);
    if (isInCatalog) return options;

    return [...options, { key: currentVoice, label: currentVoice }];
  }, [deepgramModel, deepgramVoice]);

  return (
    <div className='grid grid-cols-2 gap-4 pt-3'>
      <FormAutoComplete<IStationSchema>
        control={form.control}
        name={`${baseName}.tts.provider`}
        label='Active provider'
        placeholder='Select TTS provider'
        options={TTS_PROVIDER_OPTIONS}
        className='rounded col-span-2'
        variant='faded'
      />

      <div className='col-span-2'>
        <Accordion variant='bordered' selectionMode='multiple'>
          <AccordionItem key='elevenlabs' aria-label='ElevenLabs' title='ElevenLabs'>
            <div className='grid grid-cols-2 gap-4 pt-1'>
              <FormAutoComplete<IStationSchema>
                control={form.control}
                name={`${baseName}.tts.elevenlabs.voice`}
                label='Voice'
                isVirtualized={false}
                placeholder='Select voice'
                options={ELEVENLABS_VOICE_OPTIONS}
                variant='faded'
                className='rounded col-span-2'
              />
              <FormAutoComplete<IStationSchema>
                control={form.control}
                name={`${baseName}.tts.elevenlabs.model`}
                label='Model'
                placeholder='Select model'
                options={ELEVENLABS_MODEL_OPTIONS}
                variant='faded'
                className='rounded col-span-2'
              />
              <FormInput<IStationSchema>
                control={form.control}
                name={`${baseName}.tts.elevenlabs.stability`}
                label={labelWithTooltip(
                  'Stability',
                  'Range 0.0-1.0. Lower values sound less stable and more expressive.',
                )}
                placeholder='0.35'
                variant='faded'
                className='rounded'
              />
              <FormInput<IStationSchema>
                control={form.control}
                name={`${baseName}.tts.elevenlabs.similarity_boost`}
                label={labelWithTooltip(
                  'Similarity boost',
                  'Range 0.0-1.0. Higher values preserve the selected voice characteristics.',
                )}
                placeholder='0.75'
                variant='faded'
                className='rounded'
              />
              <FormInput<IStationSchema>
                control={form.control}
                name={`${baseName}.tts.elevenlabs.style`}
                label={labelWithTooltip(
                  'Style',
                  'Range 0.0-1.0. Controls emotional intensity in generated speech.',
                )}
                placeholder='0.60'
                variant='faded'
                className='rounded'
              />
              <FormInput<IStationSchema>
                control={form.control}
                name={`${baseName}.tts.elevenlabs.speed`}
                label={labelWithTooltip('Speed', 'Recommended range 0.7-1.2 for natural pacing.')}
                placeholder='0.85'
                variant='faded'
                className='rounded'
              />
              <FormCheckBox<IStationSchema>
                control={form.control}
                shouldUnregister={false}
                name={`${baseName}.tts.elevenlabs.use_speaker_boost`}
              >
                Use speaker boost
              </FormCheckBox>
            </div>
          </AccordionItem>

          <AccordionItem key='cartesia' aria-label='Cartesia' title='Cartesia'>
            <div className='grid grid-cols-2 gap-4 pt-1'>
              <FormAutoComplete<IStationSchema>
                control={form.control}
                name={`${baseName}.tts.cartesia.voice`}
                label='Voice'
                isVirtualized={false}
                placeholder='Select voice'
                options={CARTESIA_VOICE_OPTIONS}
                variant='faded'
                className='rounded col-span-2'
              />
              <FormAutoComplete<IStationSchema>
                control={form.control}
                name={`${baseName}.tts.cartesia.model`}
                label='Model'
                placeholder='Select model'
                options={CARTESIA_MODEL_OPTIONS}
                variant='faded'
                className='rounded col-span-2'
              />
              <FormInput<IStationSchema>
                control={form.control}
                name={`${baseName}.tts.cartesia.speed`}
                label={labelWithTooltip('Speed', 'Range 0.6-2.0. Use 1.0 for neutral pace.')}
                placeholder='1.00'
                variant='faded'
                className='rounded'
              />
              <FormInput<IStationSchema>
                control={form.control}
                name={`${baseName}.tts.cartesia.volume`}
                label={labelWithTooltip('Volume', 'Range 0.0-2.0 where 1.0 is base loudness.')}
                placeholder='1.00'
                variant='faded'
                className='rounded'
              />
              <FormAutoComplete<IStationSchema>
                control={form.control}
                name={`${baseName}.tts.cartesia.emotion`}
                label='Emotion'
                placeholder='Select emotion'
                options={CartesiaEmotionOptions}
                variant='faded'
                className='rounded'
              />
            </div>
          </AccordionItem>

          {/* <AccordionItem key='openai' aria-label='OpenAI TTS' title='OpenAI'>
            <div className='grid grid-cols-2 gap-4 pt-1'>
              <FormAutoComplete<IStationSchema>
                control={form.control}
                name={`${baseName}.tts.openai.voice`}
                label='Voice'
                placeholder='Select voice'
                options={OPENAI_VOICE_OPTIONS}
                variant='faded'
                className='rounded'
              />
              <FormAutoComplete<IStationSchema>
                control={form.control}
                name={`${baseName}.tts.openai.model`}
                label='Model'
                placeholder='Select model'
                options={OPENAI_MODEL_OPTIONS}
                variant='faded'
                className='rounded'
              />
              <FormInput<IStationSchema>
                control={form.control}
                name={`${baseName}.tts.openai.speed`}
                label={labelWithTooltip(
                  'Speed',
                  'Range 0.25-4.0. Keep near 1.0 for natural conversational voice.',
                )}
                placeholder='1.00'
                variant='faded'
                className='rounded'
              />
              <FormTextArea<IStationSchema>
                control={form.control}
                name={`${baseName}.tts.openai.instructions`}
                label='Instructions'
                placeholder='Warm, empathetic and professional clinical tone.'
                variant='faded'
                className='rounded col-span-2'
              />
            </div>
          </AccordionItem> */}

          <AccordionItem key='deepgram' aria-label='Deepgram TTS' title='Deepgram'>
            <div className='grid grid-cols-2 gap-4 pt-1'>
              <FormAutoComplete<IStationSchema>
                control={form.control}
                name={`${baseName}.tts.deepgram.voice`}
                label='Voice'
                isVirtualized={false}
                placeholder='Select voice'
                options={deepgramVoiceOptions}
                variant='faded'
                className='rounded col-span-2'
              />
              <FormAutoComplete<IStationSchema>
                control={form.control}
                name={`${baseName}.tts.deepgram.model`}
                label='Model'
                placeholder='Select model'
                options={DEEPGRAM_MODEL_OPTIONS}
                onValueChange={(newModel) => {
                  const modelKey = newModel != null && newModel !== '' ? String(newModel) : '';
                  const compatibleVoices = DEEPGRAM_VOICE_OPTIONS_BY_MODEL.get(modelKey) ?? [];
                  const currentVoice = deepgramVoice?.trim();
                  if (currentVoice && !compatibleVoices.some((voice) => voice.key === currentVoice)) {
                    form.setValue(`${baseName}.tts.deepgram.voice`, '', { shouldDirty: true });
                  }
                }}
                variant='faded'
                className='rounded col-span-2'
              />
              <FormInput<IStationSchema>
                control={form.control}
                name={`${baseName}.tts.deepgram.speed`}
                label={labelWithTooltip('Speed', 'Range 0.5-1.5. Use 1.0 for natural pace.')}
                placeholder='1.0'
                variant='faded'
                className='rounded'
              />
            </div>
          </AccordionItem>

          {/* <AccordionItem key='minimax' aria-label='MiniMax TTS' title='MiniMax'>
            <div className='grid grid-cols-2 gap-4 pt-1'>
              <FormAutoComplete<IStationSchema>
                control={form.control}
                name={`${baseName}.tts.minimax.voice`}
                label='Voice'
                placeholder='Select voice'
                options={MINIMAX_VOICE_OPTIONS}
                variant='faded'
                className='rounded'
              />
              <FormAutoComplete<IStationSchema>
                control={form.control}
                name={`${baseName}.tts.minimax.model`}
                label='Model'
                placeholder='Select model'
                options={MINIMAX_MODEL_OPTIONS}
                variant='faded'
                className='rounded'
              />
              <FormInput<IStationSchema>
                control={form.control}
                name={`${baseName}.tts.minimax.speed`}
                label='Speed'
                placeholder='1.0'
                variant='faded'
                className='rounded'
              />
              <FormInput<IStationSchema>
                control={form.control}
                name={`${baseName}.tts.minimax.pitch`}
                label='Pitch'
                placeholder='0.0'
                variant='faded'
                className='rounded'
              />
              <FormInput<IStationSchema>
                control={form.control}
                name={`${baseName}.tts.minimax.vol`}
                label='Volume'
                placeholder='1.0'
                variant='faded'
                className='rounded'
              />
              <FormAutoComplete<IStationSchema>
                control={form.control}
                name={`${baseName}.tts.minimax.emotion`}
                label='Emotion'
                placeholder='Select emotion'
                options={MINIMAX_EMOTION_OPTIONS}
                variant='faded'
                className='rounded'
              />
            </div>
          </AccordionItem> */}

          <AccordionItem key='rime' aria-label='Rime TTS' title='Rime'>
            <div className='grid grid-cols-2 gap-4 pt-1'>
              <FormAutoComplete<IStationSchema>
                control={form.control}
                name={`${baseName}.tts.rime.voice`}
                label='Voice'
                isVirtualized={false}
                placeholder='Select voice'
                options={RIME_VOICE_OPTIONS}
                variant='faded'
                className='rounded col-span-2'
              />
              <FormAutoComplete<IStationSchema>
                control={form.control}
                name={`${baseName}.tts.rime.model`}
                label='Model'
                placeholder='Select model'
                options={RIME_MODEL_OPTIONS}
                variant='faded'
                className='rounded col-span-2'
              />
              <FormInput<IStationSchema>
                control={form.control}
                name={`${baseName}.tts.rime.speed`}
                label={labelWithTooltip(
                  'Speed',
                  'Range 0.5-1.5. Keep close to 1.0 for natural British pacing.',
                )}
                placeholder='1.0'
                variant='faded'
                className='rounded'
              />
            </div>
          </AccordionItem>

          <AccordionItem key='inworld' aria-label='Inworld TTS' title='Inworld'>
            <div className='grid grid-cols-2 gap-4 pt-1'>
              <FormAutoComplete<IStationSchema>
                control={form.control}
                name={`${baseName}.tts.inworld.voice`}
                label='Voice'
                isVirtualized={false}
                placeholder='Select voice'
                options={INWORLD_VOICE_OPTIONS}
                variant='faded'
                className='rounded col-span-2'
              />
              <FormAutoComplete<IStationSchema>
                control={form.control}
                name={`${baseName}.tts.inworld.model`}
                label='Model'
                placeholder='Select model'
                options={INWORLD_MODEL_OPTIONS}
                variant='faded'
                className='rounded col-span-2'
              />
              <FormInput<IStationSchema>
                control={form.control}
                name={`${baseName}.tts.inworld.speaking_rate`}
                label={labelWithTooltip(
                  'Speaking rate',
                  'Range 0.5-1.5. Use 1.0 for neutral delivery speed.',
                )}
                placeholder='1.0'
                variant='faded'
                className='rounded'
              />
              <FormInput<IStationSchema>
                control={form.control}
                name={`${baseName}.tts.inworld.temperature`}
                label={labelWithTooltip(
                  'Temperature',
                  'Range 0.0-2.0. Lower values are more stable and consistent.',
                )}
                placeholder='1.0'
                variant='faded'
                className='rounded'
              />
            </div>
          </AccordionItem>

          <AccordionItem key='xai' aria-label='xAI TTS' title='xAI'>
            <div className='grid grid-cols-2 gap-4 pt-1'>
              <FormAutoComplete<IStationSchema>
                control={form.control}
                name={`${baseName}.tts.xai.voice`}
                isVirtualized={false}
                label='Voice'
                placeholder='Select voice'
                options={XAI_VOICE_OPTIONS}
                variant='faded'
                className='rounded col-span-2'
              />
              <FormAutoComplete<IStationSchema>
                control={form.control}
                name={`${baseName}.tts.xai.model`}
                label='Model'
                placeholder='Select model'
                options={XAI_MODEL_OPTIONS}
                variant='faded'
                className='rounded col-span-2'
              />
            </div>
          </AccordionItem>

          <AccordionItem key='fishaudio' aria-label='Fish Audio TTS' title='Fish Audio'>
            <div className='grid grid-cols-2 gap-4 pt-1'>
              <FormAutoComplete<IStationSchema>
                control={form.control}
                name={`${baseName}.tts.fishaudio.voice`}
                label='Voice'
                isVirtualized={false}
                placeholder='Select voice'
                options={fishaudioVoiceOptions}
                onValueChange={(newVoice) => {
                  const voiceId = newVoice != null && newVoice !== '' ? String(newVoice) : '';
                  if (!voiceId) return;

                  const volume = FISHAUDIO_VOICE_VOLUME_BY_ID.get(voiceId);
                  if (volume === undefined) return;

                  form.setValue(`${baseName}.tts.fishaudio.volume`, String(volume), {
                    shouldDirty: true,
                  });
                }}
                variant='faded'
                className='rounded col-span-2'
              />
              <FormAutoComplete<IStationSchema>
                control={form.control}
                name={`${baseName}.tts.fishaudio.model`}
                label='Model'
                placeholder='Select model'
                options={FISHAUDIO_MODEL_OPTIONS}
                variant='faded'
                className='rounded col-span-2'
              />
              <FormInput<IStationSchema>
                control={form.control}
                name={`${baseName}.tts.fishaudio.speed`}
                label={labelWithTooltip('Speed', 'Range 0.5-1.5. Use 1.0 for neutral delivery speed.')}
                placeholder='1.0'
                variant='faded'
                className='rounded'
              />
              <FormInput<IStationSchema>
                control={form.control}
                name={`${baseName}.tts.fishaudio.volume`}
                label={labelWithTooltip(
                  'Volume',
                  'Calibrated per voice from fish-voices.json. Locked when a catalog voice is selected.',
                )}
                placeholder='Select a voice'
                isDisabled
                variant='faded'
                className='rounded'
              />
              <FormAutoComplete<IStationSchema>
                control={form.control}
                name={`${baseName}.tts.fishaudio.latency_mode`}
                label='Latency mode'
                placeholder='Select latency mode'
                options={[
                  { label: 'Low', key: 'low' },
                  { label: 'Balanced', key: 'balanced' },
                  { label: 'Normal', key: 'normal' },
                ]}
                variant='faded'
                className='rounded'
              />
            </div>
          </AccordionItem>

          <AccordionItem key='gradium' aria-label='Gradium TTS' title='Gradium'>
            <div className='grid grid-cols-2 gap-4 pt-1'>
              <FormAutoComplete<IStationSchema>
                control={form.control}
                name={`${baseName}.tts.gradium.voice`}
                isVirtualized={false}
                label='Voice'
                placeholder='Select voice'
                options={GRADIUM_VOICE_OPTIONS}
                variant='faded'
                className='rounded col-span-2'
              />
              <FormAutoComplete<IStationSchema>
                control={form.control}
                name={`${baseName}.tts.gradium.model`}
                label='Model'
                placeholder='Select model'
                options={GRADIUM_MODEL_OPTIONS}
                variant='faded'
                className='rounded col-span-2'
              />
            </div>
          </AccordionItem>

          <AccordionItem key='gemini' aria-label='Gemini TTS' title='Gemini (British English)'>
            <div className='grid grid-cols-2 gap-4 pt-1'>
              <FormAutoComplete<IStationSchema>
                control={form.control}
                name={`${baseName}.tts.gemini.voice`}
                isVirtualized={false}
                label={labelWithTooltip(
                  'Voice',
                  'All 30 Gemini voices, spoken with a British (en-GB) accent.',
                )}
                placeholder='Select voice'
                options={GEMINI_VOICE_OPTIONS}
                variant='faded'
                className='rounded col-span-2'
              />
              <FormAutoComplete<IStationSchema>
                control={form.control}
                name={`${baseName}.tts.gemini.model`}
                label='Model'
                placeholder='Select model'
                options={GEMINI_MODEL_OPTIONS}
                variant='faded'
                className='rounded col-span-2'
              />
            </div>
          </AccordionItem>
        </Accordion>
      </div>
    </div>
  );
};
